import { useState, useEffect } from "react"
import { lumi } from "@/lib/lumi"
import {X, Mail, Lock, User, ArrowRight, Loader2} from 'lucide-react'
import toast from "react-hot-toast"

interface AuthModalProps {
  isOpen?: boolean
  onClose: () => void
  onSuccess?: () => void
}

type AuthMode = "login" | "register" | "reset"

export function AuthModal({ isOpen = true, onClose, onSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>("login")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [userName, setUserName] = useState("")
  const [otp, setOtp] = useState("")
  const [otpSent, setOtpSent] = useState(false)
  const [otpCooldown, setOtpCooldown] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (otpCooldown <= 0) return
    const timer = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000)
    return () => clearTimeout(timer)
  }, [otpCooldown])

  useEffect(() => {
    if (!isOpen) {
      setEmail("")
      setPassword("")
      setUserName("")
      setOtp("")
      setOtpSent(false)
      setOtpCooldown(0)
      setMode("login")
    }
  }, [isOpen])

  if (!isOpen) return null

  const sendOtp = async () => {
    if (!email) {
      toast.error("Please enter your email first")
      return
    }
    setLoading(true)
    try {
      await lumi.auth.sendOtp({
        email,
        type: mode === "register" ? "register" : "resetPassword",
      })
      setOtpSent(true)
      setOtpCooldown(60)
      toast.success("Verification code sent to your email!")
    } catch (error: unknown) {
      const err = error as { message?: string }
      console.error("Failed to send OTP:", error)
      toast.error(err?.message || "Failed to send verification code")
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async () => {
    if (!email || !password) {
      toast.error("Please fill in all fields")
      return
    }
    setLoading(true)
    try {
      await lumi.auth.signInWithPassword({ email, password })
      toast.success("Welcome back!")
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      const err = error as { message?: string }
      console.error("Login failed:", error)
      toast.error(err?.message || "Login failed. Please check your credentials.")
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async () => {
    if (!email || !password || !otp) {
      toast.error("Please fill in all fields including the verification code")
      return
    }
    setLoading(true)
    try {
      await lumi.auth.signUpWithPassword({ email, password, otp, userName: userName || undefined })
      toast.success("Account created! You are now signed in.")
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      const err = error as { message?: string }
      console.error("Registration failed:", error)
      toast.error(err?.message || "Registration failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async () => {
    if (!email || !password || !otp) {
      toast.error("Please fill in all fields including the verification code")
      return
    }
    setLoading(true)
    try {
      await lumi.auth.resetPassword({ email, newPassword: password, otp })
      toast.success("Password reset successful! You are now signed in.")
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      const err = error as { message?: string }
      console.error("Password reset failed:", error)
      toast.error(err?.message || "Password reset failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (mode === "login") handleLogin()
    else if (mode === "register") handleRegister()
    else handleResetPassword()
  }

  const handleGoogleLogin = async () => {
    try {
      await lumi.auth.signInWithOAuth({ provider: "google" })
    } catch (error: unknown) {
      const err = error as { message?: string }
      console.error("Google login failed:", error)
      toast.error(err?.message || "Google login failed")
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
          <h2 className="text-2xl font-bold">
            {mode === "login" ? "Welcome Back" : mode === "register" ? "Create Account" : "Reset Password"}
          </h2>
          <p className="text-emerald-100 text-sm mt-1">
            {mode === "login"
              ? "Sign in to save and manage your soap recipes"
              : mode === "register"
              ? "Join Calculator Soap to save unlimited recipes"
              : "Enter your email to reset your password"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {mode === "register" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name (optional)</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Your name"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mode === "reset" ? "New Password" : "Password"}
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {(mode === "register" || mode === "reset") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter code"
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={otpCooldown > 0 || loading}
                  className="px-4 py-2.5 bg-emerald-100 text-emerald-700 rounded-lg font-medium text-sm hover:bg-emerald-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {otpCooldown > 0 ? `${otpCooldown}s` : otpSent ? "Resend" : "Send Code"}
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || ((mode === "register" || mode === "reset") && !otpSent)}
            className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <>
                {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Reset Password"}
                <ArrowRight size={18} />
              </>
            )}
          </button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-gray-500">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>

          <div className="text-center text-sm text-gray-600 pt-2">
            {mode === "login" ? (
              <>
                <span>Don't have an account? </span>
                <button type="button" onClick={() => setMode("register")} className="text-emerald-600 font-semibold hover:underline">
                  Sign Up
                </button>
                <span className="mx-2">|</span>
                <button type="button" onClick={() => setMode("reset")} className="text-emerald-600 font-semibold hover:underline">
                  Forgot Password?
                </button>
              </>
            ) : (
              <>
                <span>Already have an account? </span>
                <button type="button" onClick={() => setMode("login")} className="text-emerald-600 font-semibold hover:underline">
                  Sign In
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
